import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Root } from "./layouts/Root";
import { ErrorBoundary } from "./ErrorBoundary";
import { useDispatch, useSelector } from "react-redux";
import {
  clearUser,
  fetchUser,
  getUser,
} from "./redux/slices/AuthUser";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Loading } from "./components/general/loading/LoadingApp";
import ProfileCompletionGate from "./components/auth/ProfileCompletionGate.jsx";

// ── Lazy-loaded pages (code-splitting) ────────────────────────────────────────
const Home = lazy(() => import("./pages/Home").then(m => ({ default: m.Home })));
const BrowseListing = lazy(() => import("./pages/BrowseListing").then(m => ({ default: m.BrowseListing })));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage").then(m => ({ default: m.CategoriesPage })));
const AboutUs = lazy(() => import("./pages/AboutUs").then(m => ({ default: m.AboutUs })));
const AddNewProduct = lazy(() => import("./pages/AddNewProduct").then(m => ({ default: m.AddNewProduct })));
const ContactUs = lazy(() => import("./pages/ContactUs").then(m => ({ default: m.ContactUs })));
const Profile = lazy(() => import("./pages/Profile").then(m => ({ default: m.Profile })));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const TransactionHistory = lazy(() => import("./pages/TransactionHistory").then(m => ({ default: m.TransactionHistory })));
const DisplayMyProducts = lazy(() => import("./pages/DisplayMyProducts").then(m => ({ default: m.DisplayMyProducts })));
const Chat = lazy(() => import("./pages/Chat").then(m => ({ default: m.Chat })));
const Payment = lazy(() => import("./pages/Payment").then(m => ({ default: m.Payment })));
const AcountInfo = lazy(() => import("./pages/AcountInfo").then(m => ({ default: m.AcountInfo })));
const SaveAddress = lazy(() => import("./pages/SaveAddress").then(m => ({ default: m.SaveAddress })));
const SuccessPayment = lazy(() => import("./pages/SuccessPayment").then(m => ({ default: m.SuccessPayment })));
const VerificationPage = lazy(() => import("./pages/VerificationPage").then(m => ({ default: m.VerificationPage })));
const FavoriteProducts = lazy(() => import("./pages/FavoriteProducts").then(m => ({ default: m.FavoriteProducts })));
const FailedPayment = lazy(() => import("./pages/FailPayment").then(m => ({ default: m.FailedPayment })));
const ContactWithUser = lazy(() => import("./pages/ContactWithUser").then(m => ({ default: m.ContactWithUser })));
const AddComment = lazy(() => import("./pages/AddComment").then(m => ({ default: m.AddComment })));
const EditComment = lazy(() => import("./pages/EditComment").then(m => ({ default: m.EditComment })));
const AddReviews = lazy(() => import("./pages/AddReviews").then(m => ({ default: m.AddReviews })));
const EditReview = lazy(() => import("./pages/EditReview").then(m => ({ default: m.EditReview })));
const RentProfile = lazy(() => import("./pages/RentProfile").then(m => ({ default: m.RentProfile })));
const EditProducts = lazy(() => import("./pages/EditProducts").then(m => ({ default: m.EditProducts })));
const ProductDetails = lazy(() => import("./pages/ProductDetails").then(m => ({ default: m.ProductDetails })));
const PopularProducts = lazy(() => import("./pages/PopularProducts").then(m => ({ default: m.PopularProducts })));
const SendReferralsCode = lazy(() => import("./pages/SendReferralsCode").then(m => ({ default: m.SendReferralsCode })));
const SignUpWithReferrals = lazy(() => import("./pages/SignUpWithReferrals").then(m => ({ default: m.SignUpWithReferrals })));
const Faq = lazy(() => import("./pages/Faq").then(m => ({ default: m.Faq })));
const TermsOfService = lazy(() => import("./pages/TermsOfService").then(m => ({ default: m.TermsOfService })));
const NotFound = lazy(() => import("./pages/NotFound").then(m => ({ default: m.NotFound })));
const Wallet = lazy(() => import("./pages/Wallet.jsx").then(m => ({ default: m.Wallet })));
const PaymentSettings = lazy(() => import("./pages/PaymentSettings.jsx").then(m => ({ default: m.PaymentSettings })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx").then(m => ({ default: m.PrivacyPolicy })));
const ResetPassword = lazy(() => import("./pages/reset-password/ResetPassword.jsx").then(m => ({ default: m.ResetPassword })));
const SendEmailResetPassword = lazy(() => import("./pages/reset-password/SendEmailResetPassword.jsx").then(m => ({ default: m.SendEmailResetPassword })));
const VerifyResetCode = lazy(() => import("./pages/reset-password/VerifyResetCode.jsx").then(m => ({ default: m.VerifyResetCode })));
const NewPassword = lazy(() => import("./pages/reset-password/NewPassword.jsx").then(m => ({ default: m.NewPassword })));

// Admin pages
const Admin = lazy(() => import("./layouts/Admin").then(m => ({ default: m.Admin })));
const AddCategories = lazy(() => import("./pages/admin/AddCategories").then(m => ({ default: m.AddCategories })));
const EditCategories = lazy(() => import("./pages/admin/EditCategories").then(m => ({ default: m.EditCategories })));
const AllUsers = lazy(() => import("./pages/admin/AllUsers").then(m => ({ default: m.AllUsers })));
const UserTransactionsPage = lazy(() => import("./pages/admin/UserTransactionsPage").then(m => ({ default: m.UserTransactionsPage })));
const EditUserPage = lazy(() => import("./pages/admin/EditUserPage").then(m => ({ default: m.EditUserPage })));
const ShowAdminCategories = lazy(() => import("./pages/admin/ShowAdminCategories").then(m => ({ default: m.ShowAdminCategories })));
const Configuration = lazy(() => import("./pages/admin/Configuration").then(m => ({ default: m.Configuration })));
const UsersReferred = lazy(() => import("./pages/admin/UsersReferred").then(m => ({ default: m.UsersReferred })));
const Reports = lazy(() => import("./pages/admin/Reports.jsx").then(m => ({ default: m.Reports })));
const DataExport = lazy(() => import("./pages/admin/DataExport.jsx").then(m => ({ default: m.DataExport })));
const DisputeCenter = lazy(() => import("./pages/admin/DisputeCenter.jsx").then(m => ({ default: m.DisputeCenter })));
const Dashboard = lazy(() => import("./pages/admin/Dashboard.jsx").then(m => ({ default: m.Dashboard })));
const AllTransactions = lazy(() => import("./pages/admin/AllTransactions.jsx").then(m => ({ default: m.AllTransactions })));
const TransactionDetail = lazy(() => import("./pages/admin/TransactionDetail.jsx").then(m => ({ default: m.TransactionDetail })));

const RequireAuth = ({ children }) => {
  const { user } = useSelector(getUser);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RequireAdmin = ({ children }) => {
  const { user } = useSelector(getUser);

  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "Admin") return <Navigate to="/" replace />;

  return children;
};

const GuestOnly = ({ children }) => {
  const { user } = useSelector(getUser);
  return user ? <Navigate to="/profile" replace /> : children;
};

const SessionBootstrap = ({ children }) => {
  const dispatch = useDispatch();
  const started = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = localStorage.getItem("token");
    if (!token) {
      dispatch(clearUser());
      setReady(true);
      return;
    }

    const restoreSession = dispatch(fetchUser()).unwrap();
    const timeout = new Promise((_, reject) => {
      setTimeout(() => reject({ status: 408, message: "Session restore timed out" }), 10000);
    });

    Promise.race([restoreSession, timeout])
      .catch((error) => {
        if (error?.status === 401 || error?.status === 403 || error?.status === 408) {
          localStorage.removeItem("token");
          dispatch(clearUser());
        }
      })
      .finally(() => setReady(true));
  }, [dispatch]);

  return ready ? children : <Loading />;
};

const Protected = ({ children }) => (
  <RequireAuth>{children}</RequireAuth>
);

const Guest = ({ children }) => <GuestOnly>{children}</GuestOnly>;

const AuthRedirect = ({ mode }) => (
  <Navigate to="/" replace state={{ authMode: mode }} />
);

export const Router = () => {
  return (
    <BrowserRouter
     future={{
      v7_startTransition: true,
      v7_relativeSplatPath:true
     }}
    >
      <SessionBootstrap>
        <ProfileCompletionGate />
        <Suspense fallback={<Loading />}>
        <Routes>
        <Route path="/" element={<Root />}>
          <Route index element={<ErrorBoundary><Home /></ErrorBoundary>} />
          <Route path="/about-us" element={<ErrorBoundary><AboutUs /></ErrorBoundary>} />
          <Route path="/faq" element={<ErrorBoundary><Faq /></ErrorBoundary>} />
          <Route path="/terms-of-service" element={<ErrorBoundary><TermsOfService /></ErrorBoundary>} />
          <Route path="/privacy-policy" element={<ErrorBoundary><PrivacyPolicy /></ErrorBoundary>} />
          <Route path="/categories" element={<ErrorBoundary><CategoriesPage /></ErrorBoundary>} />
          <Route path="/browse-listing" element={<ErrorBoundary><BrowseListing /></ErrorBoundary>} />
          <Route path="/popular-products" element={<ErrorBoundary><PopularProducts /></ErrorBoundary>} />
          <Route path="/product-detail/:productID" element={<ErrorBoundary><ProductDetails /></ErrorBoundary>} />
          <Route path="/contactWithUser/:userId/:username" element={<ErrorBoundary><ContactWithUser /></ErrorBoundary>} />
          <Route path="/contact-us" element={<ErrorBoundary><ContactUs /></ErrorBoundary>} />
          <Route path="/rent-profile/:userId" element={<ErrorBoundary><RentProfile /></ErrorBoundary>} />
          <Route path="/add-comment/:productID" element={<ErrorBoundary><AddComment /></ErrorBoundary>} />
          <Route path="/add-review/:productID" element={<ErrorBoundary><AddReviews /></ErrorBoundary>} />
          <Route path="/user/signup" element={<ErrorBoundary><SignUpWithReferrals /></ErrorBoundary>} />
          <Route path="/send-referral-code" element={<Protected><ErrorBoundary><SendReferralsCode /></ErrorBoundary></Protected>} />
          <Route path="/chat" element={<Protected><ErrorBoundary><Chat /></ErrorBoundary></Protected>} />
          <Route path="/chat/:conversationId" element={<Protected><ErrorBoundary><Chat /></ErrorBoundary></Protected>} />
          <Route path="/my-products" element={<Protected><ErrorBoundary><DisplayMyProducts /></ErrorBoundary></Protected>} />
          <Route path="/success-payment" element={<Protected><ErrorBoundary><SuccessPayment /></ErrorBoundary></Protected>} />
          <Route path="/fail-payment" element={<Protected><ErrorBoundary><FailedPayment /></ErrorBoundary></Protected>} />
          <Route path="/save-address" element={<Protected><ErrorBoundary><SaveAddress /></ErrorBoundary></Protected>} />
          <Route path="/account-info/:userId" element={<Protected><ErrorBoundary><AcountInfo /></ErrorBoundary></Protected>} />
          <Route path="/settings" element={<Protected><ErrorBoundary><Settings /></ErrorBoundary></Protected>} />
          <Route path="/edit-comment/:productID/:commentID" element={<Protected><ErrorBoundary><EditComment /></ErrorBoundary></Protected>} />
          <Route path="/add-new-product" element={<ErrorBoundary><AddNewProduct /></ErrorBoundary>} />
          <Route path="/favorite-products/:userId" element={<Protected><ErrorBoundary><FavoriteProducts /></ErrorBoundary></Protected>} />
          <Route path="/profile" element={<Protected><ErrorBoundary><Profile /></ErrorBoundary></Protected>} />
          <Route path="/editProduct/:id" element={<Protected><ErrorBoundary><EditProducts /></ErrorBoundary></Protected>} />
          <Route path="/transactions" element={<Protected><ErrorBoundary><TransactionHistory /></ErrorBoundary></Protected>} />
          <Route path="/payment/:transactionID" element={<Protected><ErrorBoundary><Payment /></ErrorBoundary></Protected>} />
          <Route path="/edit-review/:productID/:reviewID" element={<Protected><ErrorBoundary><EditReview /></ErrorBoundary></Protected>} />
          <Route path="/wallet" element={<Protected><ErrorBoundary><Wallet /></ErrorBoundary></Protected>} />
          <Route path="/payment-settings" element={<Protected><ErrorBoundary><PaymentSettings /></ErrorBoundary></Protected>} />
          <Route path="/payment-settings/payment-settings" element={<Navigate to="/payment-settings" replace />} />

          <Route path="/signup" element={<AuthRedirect mode="signup" />} />
          <Route path="/login" element={<AuthRedirect mode="login" />} />
          <Route path="/sendToken" element={<Guest><ErrorBoundary><SendEmailResetPassword /></ErrorBoundary></Guest>} />
          <Route path="/reset-password" element={<Guest><ResetPassword /></Guest>}>
            <Route index element={<ErrorBoundary><SendEmailResetPassword /></ErrorBoundary>} />
            <Route path="verify-code" element={<ErrorBoundary><VerifyResetCode /></ErrorBoundary>} />
            <Route path="new-password" element={<ErrorBoundary><NewPassword /></ErrorBoundary>} />
          </Route>
          <Route path="/verification/:method/:identifier" element={<Guest><ErrorBoundary><VerificationPage /></ErrorBoundary></Guest>} />
        </Route>

          <Route path="/admins" element={<RequireAdmin><ErrorBoundary><Admin /></ErrorBoundary></RequireAdmin>}>
            <Route index element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
            <Route path="/admins/users" element={<ErrorBoundary><AllUsers /></ErrorBoundary>} />
            <Route path="/admins/categories" element={<ErrorBoundary><ShowAdminCategories /></ErrorBoundary>} />
            <Route path="/admins/add-categories" element={<ErrorBoundary><AddCategories /></ErrorBoundary>} />
            <Route path="/admins/edit-categories/:id" element={<ErrorBoundary><EditCategories /></ErrorBoundary>} />
            <Route path="/admins/user-transactions/:userID/:username" element={<ErrorBoundary><UserTransactionsPage /></ErrorBoundary>} />
            <Route path="/admins/edit-user/:userID/:username" element={<ErrorBoundary><EditUserPage /></ErrorBoundary>} />
            <Route path="/admins/transactions" element={<ErrorBoundary><AllTransactions /></ErrorBoundary>} />
            <Route path="/admins/transactions/:transactionId" element={<ErrorBoundary><TransactionDetail /></ErrorBoundary>} />
            <Route path="/admins/configuration" element={<ErrorBoundary><Configuration /></ErrorBoundary>} />
            <Route path="/admins/Referred-Users" element={<ErrorBoundary><UsersReferred /></ErrorBoundary>} />
            <Route path="/admins/data-export" element={<ErrorBoundary><DataExport /></ErrorBoundary>} />
            <Route path="/admins/Reports" element={<ErrorBoundary><Reports /></ErrorBoundary>} />
            <Route path="/admins/disputes" element={<ErrorBoundary><DisputeCenter /></ErrorBoundary>} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </SessionBootstrap>
    </BrowserRouter>
  );
};
